/**
 * @traqly/analytics - Analytics package for PostHog integration
 *
 * Environment conditions:
 * - PostHog: Enabled for all deployed environments (production + preview).
 *   Disabled in local development (NODE_ENV=development).
 *
 * IMPORTANT: This file only exports client-safe code.
 * For server-side analytics, use @traqly/analytics/server
 */

import { trackGAEvent } from "./google/client";
import { trackMetaEvent } from "./meta/client";
import { trackPostHogClientEvent } from "./posthog/client";
import { getUtmCookieValues } from "./utm/client";

/**
 * Track an analytics event on the client-side.
 * Sends events to both PostHog and Google Analytics.
 * UTM params from cookies (set by middleware) are merged into properties.
 */
export function trackEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  const utmParams = getUtmCookieValues();
  const merged = { ...utmParams, ...properties };

  trackPostHogClientEvent(eventName, merged);
  trackGAEvent(eventName, merged);
}

// Environment utilities (client-safe)
export {
  isPostHogEnabled,
  getPostHogEnvironment,
  isMetaPixelEnabled,
  isGoogleAdsEnabled,
} from "./env";

// Client-side Meta Pixel exports
export { trackMetaEvent };

// Client-side Google Analytics exports
export {
  trackGAEvent,
  setGAUserId,
  setGAUserProperties,
  identifyGAUser,
  trackGoogleAdsConversion,
} from "./google/client";

// Client-side PostHog exports
export {
  initPostHogClient,
  isPostHogClientEnabled,
  identifyPostHogUser,
  resetPostHog,
  captureException,
} from "./posthog/client";
export { PostHogProvider } from "./posthog/provider";
